/**
 * TextField — labeled text/email/password input primitive.
 * Demonstrates the primitive conventions for an input element
 * (forwardRef to the input, prop spreading via {...rest}, error message
 * with role="alert" + aria-describedby, label/input linkage via useId).
 *
 * Plays nicely with React Hook Form: `<TextField {...register('email')} />`
 * works because RHF's register() returns onChange/onBlur/name/ref that
 * spread onto the input.
 */

import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import styles from './TextField.module.css';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  error?: string;
  hint?: string;
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASS: Record<NonNullable<TextFieldProps['size']>, string | undefined> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, size = 'md', className, id: providedId, ...rest },
  ref,
) {
  const autoId = useId();
  const id = providedId ?? `tf-${autoId}`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy =
    [error ? errorId : null, hint && !error ? hintId : null].filter(Boolean).join(' ') || undefined;

  const inputClassName = [
    styles.input,
    SIZE_CLASS[size],
    error ? styles.invalid : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <input
        ref={ref}
        id={id}
        className={inputClassName}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      {error ? (
        <div id={errorId} className={styles.errorMessage} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div id={hintId} className={styles.hint}>
          {hint}
        </div>
      ) : null}
    </div>
  );
});
